import { BrandLogo, Button, cn } from '@giyus/ui';
import {
  ActivityIcon,
  BellIcon,
  BoxesIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FileTextIcon,
  HomeIcon,
  InboxIcon,
  LandmarkIcon,
  ListTodoIcon,
  SettingsIcon,
  ShieldCheckIcon,
  ScrollTextIcon,
  TruckIcon,
  UsersIcon,
  WrenchIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { useContextStore } from '../lib/context/contextStore';
import { useActiveContext } from '../lib/context/useActiveContext';
import { usePermissions } from '../lib/permissions/usePermission';
import { buildNavItems } from './navItems';
import { activeSidebarKey, buildSidebarGroups, destinationMatches } from './sidebarNavigation';

export interface SidebarProps {
  readonly open: boolean;
  readonly drawer?: boolean;
  readonly onNavigate?: () => void;
}

const NAV_ICONS = {
  home: HomeIcon,
  'shared-mailboxes': InboxIcon,
  'research-centers': LandmarkIcon,
  studies: FileTextIcon,
  procedures: ActivityIcon,
  'participant-reimbursements': TruckIcon,
  suppliers: TruckIcon,
  people: UsersIcon,
  facilities: BoxesIcon,
  equipment: WrenchIcon,
  scheduling: CalendarDaysIcon,
  identity: UsersIcon,
  authz: ShieldCheckIcon,
  governance: ScrollTextIcon,
  documents: FileTextIcon,
  finance: LandmarkIcon,
  accounting: ScrollTextIcon,
  settings: SettingsIcon,
  'platform-health': ActivityIcon,
  workflow: ListTodoIcon,
  notifications: BellIcon,
};

export function Sidebar({ open, drawer = false, onNavigate }: SidebarProps) {
  const { t } = useTranslation(['shell', 'common']);
  const location = useLocation();
  const tenantCode = useContextStore((s) => s.activeTenantCode);
  const { context, isNetworkScope } = useActiveContext();
  const effectiveTenantCode = context?.code ?? tenantCode;
  const permissions = usePermissions();
  const items = buildNavItems(
    isNetworkScope ? 'network' : 'tenant',
    effectiveTenantCode,
    permissions,
  );
  const groups = buildSidebarGroups(items, isNetworkScope, permissions);
  const activeKey = activeSidebarKey(groups, location.pathname, location.search);
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});

  if (items.length === 0) return null;
  const homeHref =
    items[0]?.to ??
    (isNetworkScope
      ? '/network'
      : effectiveTenantCode
        ? `/t/${encodeURIComponent(effectiveTenantCode)}`
        : '/');
  const contextLabel = isNetworkScope
    ? t('shell:nav.networkContext')
    : t('shell:nav.centerContext', { name: context?.name ?? effectiveTenantCode ?? '' });

  const mainGroups = groups.filter((item) => item.key !== 'settings');
  const settingsGroup = groups.find((item) => item.key === 'settings');

  const renderGroup = (item: (typeof groups)[number]) => {
    const isActive = item.key === activeKey;
    const hasSubmenus = item.children.length > 0;
    const isExpanded = expandedModules[item.key] ?? false;
    const Icon = NAV_ICONS[item.key as keyof typeof NAV_ICONS] ?? ActivityIcon;
    const label = t(`shell:${item.labelKey}`);
    const submenuId = `${drawer ? 'mobile-' : ''}submenu-${item.key}`;
    return (
      <li key={item.key}>
        <div className={cn('giyus-nav-row', isActive && 'giyus-nav-row-active')}>
          <Link
            to={item.to}
            aria-current={isActive ? 'page' : undefined}
            onClick={onNavigate}
            className="giyus-nav-link"
            data-testid={`nav-${item.key}`}
          >
            <Icon className="size-4 shrink-0" aria-hidden="true" />
            <span className="flex-1 truncate">{label}</span>
            {item.key === 'settings' && (
              <span className="ml-auto shrink-0 text-caption font-normal text-muted-foreground">
                {t('shell:nav.portal.systemBadge')}
              </span>
            )}
          </Link>
          {hasSubmenus && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setExpandedModules((prev) => ({ ...prev, [item.key]: !isExpanded }));
              }}
              aria-label={t('shell:nav.toggleSubmenu', { module: label })}
              aria-expanded={isExpanded}
              aria-controls={submenuId}
              className="size-8 shrink-0 self-center text-muted-foreground opacity-60 hover:opacity-100"
              data-testid={`nav-toggle-${item.key}`}
            >
              {isExpanded ? (
                <ChevronDownIcon aria-hidden="true" />
              ) : (
                <ChevronRightIcon aria-hidden="true" />
              )}
            </Button>
          )}
        </div>
        {hasSubmenus && (
          <ul
            id={submenuId}
            hidden={!isExpanded}
            className="giyus-nav-group-children"
            data-testid={`nav-submenus-${item.key}`}
          >
            {item.children.map((sub) => {
              const active = destinationMatches(sub.to, location.pathname, location.search, true);
              return (
                <li key={sub.id}>
                  <Link
                    to={sub.to}
                    onClick={onNavigate}
                    aria-current={active ? 'page' : undefined}
                    className={cn('giyus-nav-item', active && 'giyus-nav-item-active')}
                    data-testid={
                      sub.id === 'procurement' || sub.id === 'accounting'
                        ? `nav-${sub.id}`
                        : `nav-sub-${sub.id}`
                    }
                  >
                    {t(`shell:${sub.labelKey}`)}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  };

  return (
    <nav
      id={drawer ? 'mobile-sidebar' : 'sidebar'}
      aria-label={t('common:a11y.mainNavigation')}
      className={cn(
        'giyus-nav-rail flex-col gap-4 px-3.5 py-5',
        drawer
          ? 'flex h-full min-h-0 w-full overflow-y-auto border-r-0'
          : 'sticky top-0 h-svh w-(--shell-sidebar-w) shrink-0 overflow-y-auto md:flex',
        !drawer && (open ? 'flex' : 'hidden'),
      )}
      data-testid={drawer ? 'mobile-sidebar' : 'sidebar'}
    >
      <Link
        to={homeHref}
        aria-label={t('shell:nav.home')}
        className="giyus-brand no-underline"
        onClick={onNavigate}
        data-testid={drawer ? 'mobile-sidebar-brand-home-link' : 'sidebar-brand-home-link'}
      >
        <BrandLogo symbol className="size-8 shrink-0" />
        <div className="min-w-0">
          <strong>GIYUS</strong>
          <span className="block text-caption text-muted-foreground">{contextLabel}</span>
        </div>
      </Link>
      <ul className="flex flex-col gap-1">{mainGroups.map(renderGroup)}</ul>
      {settingsGroup && (
        <ul className="mt-auto flex flex-col gap-1 border-t border-border pt-3">
          {renderGroup(settingsGroup)}
        </ul>
      )}
    </nav>
  );
}
